/**
 * scriptRequestMutations.js — the host side of `req.setHeader()` & co.
 *
 * A pre-request script (Bruno's `req.setHeader/deleteHeader/setHeaders/
 * setBody/setMethod`) may change the request it runs before. The sandbox
 * worker reports what it changed as `SCRIPT_RESULT.requestMutations`:
 *
 *   { method?: string, headers?: { set: {name: value}, remove: string[] }, body?: string }
 *
 * THE WORKER RUNS USER CODE, SO THAT FIELD IS UNTRUSTED INPUT. Everything is
 * re-validated here; the worker's own checks are a courtesy to the script
 * author, this module is the control:
 *
 *   - header names must be RFC 7230 tokens; framing / routing headers
 *     (Host, Content-Length, Transfer-Encoding, Connection, …) are refused;
 *   - a header VALUE with CR, LF or another control character is refused
 *     (header injection / request splitting);
 *   - bounded: at most MAX_HEADERS headers, MAX_HEADER_VALUE_BYTES per value,
 *     MAX_BODY_BYTES of body;
 *   - the method must be one of the methods Apiboo sends;
 *   - there is NO url field. A script silently re-pointing a request at another
 *     host after the user's auth is attached would be an exfiltration
 *     primitive, so `req.setUrl()` refuses in the worker and a forged `url`
 *     is dropped here.
 *
 * The call sites apply mutations as the LAST step before the send, after
 * `{{var}}` substitution and auth, and never substitute them again — a script
 * cannot route a `{{withheld_secret}}` into a header that way.
 */
import { utf8ByteLength } from './formatBytes';
import { assembleRequestParts } from './requestAssembly';

export const MAX_HEADERS = 100;
export const MAX_HEADER_NAME_LENGTH = 256;
export const MAX_HEADER_VALUE_BYTES = 16 * 1024;
export const MAX_BODY_BYTES = 5 * 1024 * 1024;
export const ALLOWED_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'];

const HEADER_TOKEN = /^[!#$%&'*+.^_`|~0-9A-Za-z-]+$/;
// HTAB is legal inside a header value; every other C0 control and DEL is not.
// eslint-disable-next-line no-control-regex
const HEADER_BAD_VALUE = /[\u0000-\u0008\u000a-\u001f\u007f]/;
const FORBIDDEN_HEADERS = new Set([
  'host', 'content-length', 'transfer-encoding', 'connection',
  'keep-alive', 'upgrade', 'te', 'trailer',
]);

const isPlainObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * True when `str` is over `cap` UTF-8 bytes. The two length checks settle
 * almost every call without encoding: UTF-8 bytes are >= the UTF-16 length
 * and <= 3x it. Encoding a 200 MB body just to learn it is too big cost
 * ~0.5 s of main thread (tester F2).
 */
function overBytes(str, cap) {
  if (str.length > cap) return true;
  if (str.length * 3 <= cap) return false;
  return utf8ByteLength(str) > cap;
}

function headerNameProblem(name) {
  if (typeof name !== 'string' || name.length === 0) return 'a header name must be a non-empty string';
  if (name.length > MAX_HEADER_NAME_LENGTH) return `header name longer than ${MAX_HEADER_NAME_LENGTH} characters`;
  if (!HEADER_TOKEN.test(name)) return `invalid header name ${JSON.stringify(name)}`;
  if (FORBIDDEN_HEADERS.has(name.toLowerCase())) return `the "${name}" header cannot be set by a script`;
  return null;
}

/**
 * Validate a raw `requestMutations` value from the sandbox.
 *
 * @param {*} raw
 * @returns {{ mutations: ({method?: string, headers?: {set: object, remove: string[]}, body?: string}|null), dropped: string[] }}
 *   `mutations` is null when nothing valid is left; `dropped` names every
 *   refused part, for the user.
 */
export function sanitizeRequestMutations(raw) {
  const dropped = [];
  if (raw === null || raw === undefined) return { mutations: null, dropped };
  if (!isPlainObject(raw)) return { mutations: null, dropped: ['request changes from the script were not an object'] };

  const out = {};

  for (const key of Object.keys(raw)) {
    if (key !== 'method' && key !== 'headers' && key !== 'body') {
      dropped.push(key === 'url'
        ? 'a script may not change the request URL'
        : `unknown request change "${key}"`);
    }
  }

  if (raw.method !== undefined) {
    const m = typeof raw.method === 'string' ? raw.method.toUpperCase() : null;
    if (m && ALLOWED_METHODS.includes(m)) out.method = m;
    else dropped.push(`unsupported method ${JSON.stringify(raw.method)}`);
  }

  if (raw.headers !== undefined) {
    const h = raw.headers;
    if (!isPlainObject(h)) {
      dropped.push('header changes were not an object');
    } else {
      const setIn = isPlainObject(h.set) ? h.set : {};
      const removeIn = Array.isArray(h.remove) ? h.remove : [];
      if (h.set !== undefined && !isPlainObject(h.set)) dropped.push('header "set" was not an object');
      if (h.remove !== undefined && !Array.isArray(h.remove)) dropped.push('header "remove" was not a list');

      // At most MAX_HEADERS changes are ACCEPTED (sets first, then removes, in
      // order); the excess is refused with one reason — never all of them.
      let accepted = 0;
      let excess = 0;
      const set = {};
      const seen = new Map(); // lower-case → the key kept in `set`
      for (const name of Object.keys(setIn)) {
        const value = setIn[name];
        const problem = headerNameProblem(name);
        if (problem) { dropped.push(problem); continue; }
        if (typeof value !== 'string') { dropped.push(`header "${name}" value is not a string`); continue; }
        if (HEADER_BAD_VALUE.test(value)) { dropped.push(`header "${name}" has a control character (CR/LF) in its value`); continue; }
        if (overBytes(value, MAX_HEADER_VALUE_BYTES)) { dropped.push(`header "${name}" value is larger than ${MAX_HEADER_VALUE_BYTES} bytes`); continue; }
        const lc = name.toLowerCase();
        if (seen.has(lc)) {
          delete set[seen.get(lc)];
        } else if (accepted >= MAX_HEADERS) {
          excess += 1;
          continue;
        } else {
          accepted += 1;
        }
        seen.set(lc, name);
        set[name] = value;
      }
      const remove = [];
      for (const name of removeIn) {
        const problem = headerNameProblem(name);
        if (problem) { dropped.push(problem); continue; }
        const lc = name.toLowerCase();
        if (remove.includes(lc)) continue;
        if (accepted >= MAX_HEADERS) { excess += 1; continue; }
        accepted += 1;
        remove.push(lc);
      }
      if (excess > 0) dropped.push(`${excess} header change(s) over the limit of ${MAX_HEADERS} were refused`);
      if (Object.keys(set).length > 0 || remove.length > 0) out.headers = { set, remove };
    }
  }

  if (raw.body !== undefined) {
    if (typeof raw.body !== 'string') dropped.push('the new body is not a string');
    else if (overBytes(raw.body, MAX_BODY_BYTES)) dropped.push(`the new body is larger than ${MAX_BODY_BYTES} bytes`);
    else out.body = raw.body;
  }

  return { mutations: Object.keys(out).length > 0 ? out : null, dropped };
}

function deleteHeaderCaseInsensitive(headers, name) {
  const lc = name.toLowerCase();
  for (const key of Object.keys(headers)) {
    if (key.toLowerCase() === lc) delete headers[key];
  }
}

/**
 * Apply one mutation result, or a list of them in order (the collection
 * runner's collection → folder → request pre-request chain), to the request
 * that is about to go out. Sanitizes every entry itself, so no call site can
 * forget to.
 *
 * @param {{method: string, headers: object, body: (string|null|undefined), multipart: (Array|null|undefined)}} request
 * @param {*|Array<*>} rawMutations
 * @returns {{method: string, headers: object, body: *, multipart: *, notes: string[]}}
 *   a NEW object; the input is not modified.
 */
export function applyRequestMutations(request, rawMutations) {
  let { method, body } = request;
  const { multipart } = request;
  const headers = { ...(request.headers || {}) };
  const notes = [];

  const list = Array.isArray(rawMutations) ? rawMutations : [rawMutations];
  for (const raw of list) {
    const { mutations, dropped } = sanitizeRequestMutations(raw);
    notes.push(...dropped);
    if (!mutations) continue;

    if (mutations.method) method = mutations.method;
    if (mutations.headers) {
      for (const name of mutations.headers.remove) deleteHeaderCaseInsensitive(headers, name);
      for (const [name, value] of Object.entries(mutations.headers.set)) {
        deleteHeaderCaseInsensitive(headers, name);
        headers[name] = value;
      }
    }
    if (mutations.body !== undefined) {
      if (Array.isArray(multipart) && multipart.length > 0) {
        notes.push('req.setBody() was ignored: the request has a multipart form body');
      } else {
        body = mutations.body;
      }
    }
  }

  return { method, headers, body, multipart, notes };
}

/**
 * The body a PRE-REQUEST script sees as `req.getBody()`: the request's body
 * as typed — built by the same assembly Send uses, but with NO variable
 * substitution, as Bruno interpolates only after the script. A string, or
 * `undefined` for a multipart form (the worker then refuses getBody()).
 * Withheld secrets stay withheld: an unsubstituted body holds `{{name}}`, not
 * the value, and buildSandboxPayload redacts the request body string anyway.
 *
 * @param {object} request  the editor/sidebar request shape (assembleRequestParts)
 * @returns {string|undefined}
 */
export function preRequestScriptBody(request) {
  try {
    const { body, multipart } = assembleRequestParts(request);
    if (Array.isArray(multipart)) return undefined;
    return typeof body === 'string' ? body : '';
  } catch {
    return undefined;
  }
}
